import './SavedViewsPanelComponent.scss';
// pure
import { PageStateList } from '@shared/user-state/ui/list/PageStateList';
import { childFilterPresets, Tree as TreeNative, TreeNode } from '@shared/ui-native/Tree';
import { PageStateEditor } from '@shared/user-state/ui/PageStateEditor';
import { Button } from '@shared/ui-react/Button';
import { PageStateShare } from '@shared/user-state/ui/PageStateShare';
import { MapStatePartialLoad } from '@shared/user-state/map/MapStatePartialLoad';
import { Catch } from '@shared/ui-react/Catch';
import { PageStateFacade } from '@shared/user-state/PageStateFacade';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { PageStateUtils } from '@shared/user-state/PageStateUtils';
import { cs } from '@shared/utils/utils-classname';
import { Tree as TreeDS, type TreeNodeData, collectAllFolderIds } from 'design-system';
import { TreeToolbar } from '@shared/ui-react/TreeToolbar';
// libs
import * as React from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { LStorage } from '@shared/Storage';
import { MapPanelFilter } from '@shared/map/MapPanelFilter';
import { SavedViewsPanelFilterComponent } from '@shared/user-state/ui/SavedViewsPanelFilterComponent';
import { PageStateService } from '@shared/user-state/PageStateService';
import { Telemetry, TelemetryEventType, TelemetrySource } from '@shared/Telemetry';
import { DialogConfirm } from '@shared/ui-native/Dialog';
import { Notify } from '@shared/ui-native/Notify';
import { Events } from '@shared/Events';
import { IContextMenuItem, renderContextMenuAtPosition } from '@shared/ui-react/ContextMenu';

export interface ISavedViewsFiles {
    [key: number]: { headUuid: string; name: string };
}

@observer
export class SavedViewsPanelComponent extends React.Component<{
    pageStateFacade: PageStateFacade<IMapPageState>;
    treeData: ITreeData;
    hasPartialLoadDialog: boolean;
    toggleExpandPanel: () => void;
    onDownload: (state: IPageState) => void;
    closeMobileSavedViewPanel?: () => void;
    pinLeftOffset?: number;
    mobileView?: boolean;
    canShowMarkersOnMap?: boolean;
}> {
    @observable isTwoColumns = false;
    @observable view: 'thumbs' | 'list' = 'thumbs';
    @observable isRendered = false;
    @observable private treeNodes: TreeNodeData[] = [];
    @observable private filterText = '';
    @observable private expandedIds: (number | string)[] = [];
    @observable private hideEmptyFolders = true;
    @observable private expandOnStart = 'firstLevel';

    resizeObserver = null;
    files: ISavedViewsFiles = [];
    fileNamesByHeadUuid: Record<string, string> = {};
    store = this.props.pageStateFacade.store;

    private nativeTree: TreeNative = null;
    private panelWrapper = React.createRef<HTMLDivElement>();

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        // two columns panel
        try {
            this.resizeObserver = new ResizeObserver((entries, observer) => {
                this.isTwoColumns = entries[0].contentRect.width > 570;
            });
            this.resizeObserver.observe(this.panelWrapper.current);
        } catch {}

        if (this.props.treeData) {
            this.calcModelIds(this.props.treeData);
            this.initTree();
        }
    }

    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.treeData) {
            this.calcModelIds(this.props.treeData);
        }

        if (!this.nativeTree && prevProps.treeData !== this.props.treeData) {
            this.initTree();
        }
    }

    render() {
        const items = this.store.selectedFolderItems;
        const allFolderItemsCount = this.store.counts.allFolderCounts[this.store.selectedFolderId] || 0;
        const visibleFolderItemsCount = this.store.selectedFolderItems?.length || 0;
        const hiddenCount = allFolderItemsCount - visibleFolderItemsCount;
        const isThumbs = this.view === 'thumbs';

        const HiddenByFilterBanner = (
            <div className='flex-row ai-center gap-half' style={{ justifyContent: 'center' }}>
                <div>{__('general.n_hidden_by_filter', hiddenCount)}</div>
                <Button secondary small onClick={() => (this.store.showAllFolderContent = true)}>
                    {__('general.show_all')}
                </Button>
            </div>
        );

        return (
            <>
                <header className='panel-title'>{__('menu.saved_views')}</header>

                <nav className='sidebar-toolbar'>
                    <Button
                        secondary
                        subtle
                        onClick={() => this.onStateCreateOrEdit(null)}
                        loading={this.store.asyncState.isCreating}
                        disabled={this.store.isDoingAsyncWork || !this.store.selectedFolderId}
                    >
                        {__('js_saved_views.save_view')}
                    </Button>
                    <Dropdown closeOnBodyClick={false} buttonStyle={{ paddingLeft: 10, paddingRight: 10 }} className='ik-button-secondary--subtle'>
                        <i className='iki iki-more' />
                        <div>
                            <div className='dropdown-list-title'>{__('general.sort')}:</div>
                            <ul className='dropdown-list clickable'>
                                <li onClick={() => (this.store.sortKey = 'name')}>
                                    <input type='radio' checked={this.store.sortKey == 'name'} readOnly />
                                    {__('general.name')}
                                </li>
                                <li onClick={() => (this.store.sortKey = 'timestamp')}>
                                    <input type='radio' checked={this.store.sortKey == 'timestamp'} readOnly />
                                    {__('general.date')}
                                </li>
                            </ul>
                        </div>
                    </Dropdown>
                </nav>
                <div className={cs('saved-views-panel__wrapper', { 'two-col': this.isTwoColumns })} ref={this.panelWrapper}>
                    <Catch>
                        <section className='saved-views__controls__wrapper'>
                            {!this.props.mobileView && this.isRendered && (
                                <div className='flex-row gap-half ai-center' style={{ padding: '4px 8px' }}>
                                    <div className='tree-toolbar-wrapper centered' style={{ display: 'block', flexGrow: 1, overflow: 'hidden' }}>
                                        <TreeToolbar
                                            filterText={this.filterText}
                                            onFilterChange={text => {
                                                this.filterText = text;
                                            }}
                                            onExpandAll={() => {
                                                this.expandedIds = Array.from(collectAllFolderIds(this.treeNodes));
                                            }}
                                            onCollapseAll={() => {
                                                this.expandedIds = [];
                                            }}
                                            hideEmptyFolders={!this.hideEmptyFolders}
                                            hideEmptyFoldersAlways={false}
                                            onToggleHideEmptyFolders={() => {
                                                this.hideEmptyFolders = !this.hideEmptyFolders;
                                                this.nativeTree?.toggleHideEmptyFolders();
                                                setTimeout(() => this.refreshNodes(), 50);
                                            }}
                                            expandOnStart={this.expandOnStart}
                                            onSetExpandOnStart={value => {
                                                this.expandOnStart = value;
                                                this.nativeTree?.setExpandOnStart(value as any);
                                            }}
                                            showContextMenuCreateFolder={true}
                                            onOpenCreateFolder={() => {
                                                this.nativeTree?.openCreateFolder(this.nativeTree.getSelected() ?? this.nativeTree.root);
                                            }}
                                            hasContentCounts={true}
                                        />
                                    </div>
                                    <MapPanelFilter
                                        pinned={this.store.filterPinned}
                                        pinLeftOffset={this.props.pinLeftOffset}
                                        opened={this.store.filterOpened}
                                        activeFiltersCount={this.store.numberOfAppliedFilters}
                                        clearFilter={this.store.clearFilter}
                                        togglePinned={() => {
                                            this.store.filterPinned = !this.store.filterPinned;
                                            this.store.filterOpened = true;
                                        }}
                                        toggleOpened={() => (this.store.filterOpened = !this.store.filterOpened)}
                                    >
                                        <SavedViewsPanelFilterComponent store={this.store} canShowMarkersOnMap={this.props.canShowMarkersOnMap} mobile={false} />
                                    </MapPanelFilter>
                                </div>
                            )}

                            <TreeDS
                                treeData={this.treeNodes}
                                showRoot={true}
                                selectedId={this.store.selectedFolderId}
                                expandedIds={this.expandedIds}
                                onExpandedIdsChange={ids => {
                                    this.expandedIds = ids;
                                }}
                                filterText={this.filterText}
                                onNodeSelected={node => {
                                    this.store.setSelectedFolderId(node.id as number);
                                }}
                                hideEmptyFolders={!this.hideEmptyFolders}
                                onContextMenu={this.handleContextMenu}
                                renderLabel={node => {
                                    const folderId = node.id as number;
                                    const total = this.store.counts.allFolderCounts[folderId] ?? 0;
                                    const filtered = this.store.counts.filteredFolderCounts[folderId] ?? 0;
                                    const showFiltered = filtered !== total;

                                    let countText = '';
                                    if (total > 0) {
                                        countText += showFiltered ? `(${filtered}/${total})` : `(${total})`;
                                    }
                                    if (!countText) return node.name;
                                    return (
                                        <>
                                            {node.name} <span style={{ color: 'var(--ik-color-text-weakest)' }}>{countText}</span>
                                        </>
                                    );
                                }}
                            />
                        </section>

                        <section className='saved-views__list__wrapper'>
                            {this.store.asyncState.isFetching && <div style={{ height: 200 }} className='overlay-loading' />}

                            {!this.store.asyncState.isFetching && this.store.selectedFolderId && visibleFolderItemsCount === 0 && (
                                <div className='text-center padded'>
                                    <div>{__('general.folder_is_empty')}</div>
                                    {hiddenCount > 0 ? HiddenByFilterBanner : null}
                                </div>
                            )}

                            {!this.store.asyncState.isFetching && this.store.selectedFolderId && items.length > 0 && (
                                <>
                                    <nav className='flex-row' style={{ flexShrink: 0 }}>
                                        <div style={{ marginLeft: 'auto' }}>
                                            <Button
                                                onClick={this.toggleView}
                                                icon={isThumbs ? 'iki iki-menu' : 'iki iki-grid'}
                                                title={isThumbs ? 'List' : 'Thumbnails'}
                                                className='ik-button-secondary--subtle ik-button-small'
                                            />
                                            {!this.isMobile && (
                                                <button onClick={this.props.toggleExpandPanel} className='ik-button-secondary--subtle ik-button-small'>
                                                    <i className='iki iki-expand' />
                                                </button>
                                            )}
                                            <button onClick={this.closeThumbnails} className='ik-button-secondary--subtle ik-button-small' title={__('general.close')}>
                                                <i className='iki iki-cross' />
                                            </button>
                                        </div>
                                    </nav>
                                    {hiddenCount > 0 ? HiddenByFilterBanner : null}
                                    <PageStateList
                                        isLoading={this.store.isDoingAsyncWork}
                                        animate={!this.store.asyncState.isFetching}
                                        items={items}
                                        view={this.view}
                                        onClick={this.onStateClick}
                                        onEdit={this.onStateCreateOrEdit}
                                        onUpdateState={this.onStateUpdate}
                                        onDelete={this.onStateDelete}
                                        onShare={this.onStateShare}
                                        onDownload={this.props.onDownload}
                                        onDetails={this.props.hasPartialLoadDialog ? this.onStateDetails : null}
                                        onOpenInOtherMap={this.onOpenInOtherMap}
                                        onLockToggle={this.onLockToggle}
                                        models={this.files}
                                        focusedId={this.props.pageStateFacade.store.focusItemId}
                                        modelNames={this.fileNamesByHeadUuid}
                                    />
                                </>
                            )}
                        </section>
                    </Catch>
                </div>
            </>
        );
    }

    private handleContextMenu = (node: TreeNodeData, position: { x: number; y: number }) => {
        const items: IContextMenuItem[] = [];
        const nativeNode = node.data as TreeNode;
        const isMobilePage = window.location.href.includes('mobile');

        if (node.type === 'folder' && !isMobilePage && !nativeNode.state?.readOnly) {
            items.push({
                label: __('js_files.create_folder'),
                icon: 'iki iki-folder-closed',
                action: () => this.nativeTree.openCreateFolder(nativeNode),
            });
        }

        if (!items.length) return;

        Events.trigger('map-hide-overlays');
        renderContextMenuAtPosition(items, position.x, position.y, 'tree-context-menu', () => {});
    };

    private convertNode = (node: TreeNode): TreeNodeData => ({
        id: node.id,
        name: node.ajaxData?.name ?? node.text ?? '',
        type: 'folder',
        children: node.children?.filter(c => !c.state.hidden).map(this.convertNode),
        data: node,
    });

    private refreshNodes = action(() => {
        this.treeNodes = this.nativeTree ? [this.convertNode(this.nativeTree)] : [];
    });

    private initTree(): void {
        if (this.nativeTree) return;

        this.nativeTree = new TreeNative({
            renderView: false,
            showRoot: true,
            showCheckboxes: false,
            showContextMenuCreateFolder: true,
            childFilter: childFilterPresets.onlyFolders,
            hideEmptyFolders: true,
            fetchContentCountsFn: () => {
                return PageStateService.fetchFolderCounts(this.store.page, kuura.getRootFolderId());
            },
            storageKey: 'saved-views-tree',
        });

        this.nativeTree.on('render', () => {
            setTimeout(() => {
                action(() => {
                    this.isRendered = true;
                })();
                this.refreshNodes();

                if (this.store.stateful.queuedRestoreState) {
                    this.store.stateful.restoreState(this.store.stateful.queuedRestoreState);
                    this.store.stateful.queuedRestoreState = null;
                }
            });
        });

        this.nativeTree.initAndRender(this.props.treeData);

        // Sync persisted settings from native tree to MobX store
        const storedState = this.nativeTree.getStoredState();
        if (storedState?.expandOnStart) {
            this.expandOnStart = storedState.expandOnStart;
        }
        if (storedState?.hideEmptyFolders != null) {
            this.hideEmptyFolders = storedState.hideEmptyFolders;
        }
        const expandedDsIds: (number | string)[] = [];
        this.nativeTree.each(c => {
            if (c.isFolder() && c.state.expanded) {
                expandedDsIds.push(c.id);
            }
        }, true);
        this.expandedIds = expandedDsIds;
    }

    calcModelIds(node) {
        this.eachPostOrder(node, child => {
            if (child.models) {
                child.models.forEach(model => {
                    this.files[model.id] = { headUuid: model.headUuid, name: model.name };
                    this.fileNamesByHeadUuid[model.headUuid] = model.name;
                });
            }
        });
    }

    eachPostOrder(node, callback: (node) => void) {
        for (const child of node.folders) {
            this.eachPostOrder(child, callback);
        }
        callback(node);
    }

    onStateClick = (state: IPageState) => {
        !!this.props.closeMobileSavedViewPanel && this.props.closeMobileSavedViewPanel();

        if (state.page === 'MAP3D_LEGACY') {
            this.props.pageStateFacade.handler.restoreState(state);
        } else {
            this.store.fetchItem(state.uuid).then((state: IMapPageState) => {
                // dont switch panels when clicking on item
                if (state.page === 'MAP') {
                    state.data.panel.activePanel = null;
                    if (state.data.panel.savedViews) {
                        // dont switch folders when manually clicked, state was maybe moved to another folder and will open the original folder
                        state.data.panel.savedViews.selectedFolderId = null;
                    }
                }
                this.props.pageStateFacade.handler.restoreState(state);
            });
        }
    };

    onStateCreateOrEdit = async (baseState?: IPageState) => {
        Telemetry.add(baseState ? TelemetryEventType.SAVED_VIEWS_EDIT : TelemetryEventType.SAVED_VIEWS_CREATE, TelemetrySource.MAP_2D_DESKTOP);

        const currentState = await this.props.pageStateFacade.handler.getCurrentState(true);

        if (!baseState) {
            baseState = currentState;
            baseState.name = '';
            baseState.liveState = false;
            baseState.visibility = 'PUBLIC';
            baseState.folderId = this.store.selectedFolderId;
        }

        PageStateEditor.open(baseState, currentState, this.props.treeData, fullState => {
            return fullState.uuid ? this.store.update(fullState) : this.store.create(fullState);
        });
    };

    onStateDelete = (state: IPageState) => {
        if (!confirm(__('general.confirm_delete', state.name))) return false;

        this.store.remove(state.id).then(r => {
            const wrapper = document.getElementById('view-properties-wrapper');
            if (wrapper) {
                ReactUtils.safeUnmountComponentAtNode(wrapper);
            }
        });
    };

    onStateDetails = (state: IPageState) =>
        MapStatePartialLoad.open(state.uuid, state => {
            this.props.pageStateFacade.handler.restoreState(state);
        });

    onStateShare = (state: IPageState) => {
        Telemetry.add(TelemetryEventType.SAVED_VIEWS_SHARE, TelemetrySource.MAP_2D_DESKTOP);
        PageStateShare.open(state);
    };

    onStateUpdate = async (state: IPageState) => {
        if (confirm(__('js_saved_views.update_with_current_map_state'))) {
            const currentState = await this.props.pageStateFacade.handler.getCurrentState(true);
            const fullState = {
                ...state,
                data: currentState.data,
            };
            this.store.update(fullState as IMapPageState);
        }
    };

    onOpenInOtherMap = (state: IPageState) => PageStateUtils.openStateInOtherMap(state);

    onLockToggle = (state: IPageState) => {
        const isLocked = !!state.lockedAt;
        const msg = isLocked ? __('js_saved_views.confirm_unlock') : __('js_saved_views.confirm_lock');

        DialogConfirm({ msg }, () => {
            const lockAction = isLocked ? PageStateService.unlock(state.uuid) : PageStateService.lock(state.uuid);
            return lockAction
                .then(() => {
                    Notify.success();
                    this.store.refresh();
                })
                .catch(e => {
                    Notify.error();
                    console.log('onLockToggle error:', e);
                });
        });
    };

    toggleView = () => {
        this.view = this.view === 'thumbs' ? 'list' : 'thumbs';
        LStorage.setItem('map-panel-' + '_saved-views-view', this.view);
    };

    closeThumbnails = () => {
        this.store.setSelectedFolderId(null);
    };

    get isMobile(): boolean {
        return window.innerWidth < 700;
    }
}
